import { useCallback } from "react";
import { pricingUtils } from "@flights/web-api-utils-universal";
import { type UIPrice } from "@flights/types";

import useTrackerContext from "hooks/useTrackerContext";
import useGlobalContext from "hooks/useGlobalContext";
import useRouteName from "hooks/useRouteName";
import { type RouteName } from "app/routes";

const ACTION_NAME = "flights__price_rendered";
const ACTION_VERSION = "1.0.0";

export type PriceRenderedUiComponent = "BREAKDOWN" | "TAG";

export type PriceRenderedProductPrice = {
  productType: string;
  productPrice: UIPrice;
};

type PriceRenderedPrice = {
  currency_code?: string;
  price?: string;
};

type PriceRenderedProduct = {
  price?: PriceRenderedPrice;
  product_id?: string;
  product_type?: string;
};

type PriceRenderedContent = {
  funnel_page: RouteName;
  products?: PriceRenderedProduct[];
  request_id: string;
  session_id?: string;
  total_price: PriceRenderedPrice;
  ui_component: PriceRenderedUiComponent;
};

type Content = PriceRenderedContent;

type TrackingData = {
  analyticSessionId?: string;
  requestId: string;
};

export const usePriceConsistencyMonitoringEvents = () => {
  const { trackExternal } = useTrackerContext();
  const routName = useRouteName();
  const { analyticSessionId, requestId } = useGlobalContext();

  return useCallback(
    (
      uiComponent: PriceRenderedUiComponent,
      productId: string,
      productPrices: PriceRenderedProductPrice[],
      totalPrice: UIPrice
    ) => {
      const trackingData: TrackingData = { analyticSessionId, requestId };
      const content = getContent(routName, trackingData, uiComponent, productId, productPrices, totalPrice);

      trackExternal({
        type: "c360",
        args: [
          {
            action_name: ACTION_NAME,
            action_version: ACTION_VERSION,
            content
          }
        ]
      });
    },
    [analyticSessionId, requestId, routName, trackExternal]
  );
};

const getContent = (
  routeName: RouteName,
  trackingData: TrackingData,
  uiComponent: PriceRenderedUiComponent,
  productId: string,
  productPrices: PriceRenderedProductPrice[],
  totalPrice: UIPrice
): Content => {
  const products: PriceRenderedProduct[] = productPrices.map(({ productPrice, productType }) => ({
    price: {
      currency_code: productPrice.currencyCode,
      price: String(pricingUtils.priceToValue(productPrice))
    },
    product_id: productId,
    product_type: productType
  }));
  const total = {
    currency_code: totalPrice.currencyCode,
    price: String(pricingUtils.priceToValue(totalPrice))
  };

  return {
    funnel_page: routeName,
    products,
    request_id: trackingData.requestId,
    session_id: trackingData.analyticSessionId,
    total_price: total,
    ui_component: uiComponent
  };
};

export default usePriceConsistencyMonitoringEvents;
